import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Save, X } from 'lucide-react';

interface Role {
  id: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

const roleLabels: Record<string, string> = {
  admin: 'مدير النظام',
  ceo: 'المدير التنفيذي',
  delegate: 'مندوب',
  merchant: 'تاجر',
  staff: 'موظف',
  support: 'الدعم',
};

const roleBackendIds: Record<string, string> = {
admin: 'admin-role-001',
ceo: 'ceo-role-001',
delegate: 'delegate-role-001',
merchant: 'merchant-role-001',
staff: 'staff-role-001',
support: 'support-role-001',
};

export default function EditUserPage() {
  const { id } = useParams();
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);

  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    roleId: '',
    branchId: '',
    isActive: true
  });

  useEffect(() => {
    if (id) {
      loadUser();
      loadRoles();
    }
  }, [id]);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);

      const response = await api.get('/api/Roles');

      const data = response.data?.data;

      const roleList = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];

      setRoles(roleList);
    } catch (error) {
      console.error('Error loading roles:', error);
      alert('حدث خطأ أثناء تحميل الأدوار');
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const response = await api.get(`/api/users/${id}`);
      const user = response.data.data;

      /*
       * بعض إصدارات الـ API قد ترجع roleId،
       * وبعضها قد ترجع role / roleName.
       *
       * نحاول استخدام الـ ID أولاً، ثم نبحث عن الدور بالاسم.
       */
      let roleId = user.roleId?.toString() || '';

  const legacyRoleIds: Record<string, string> = {
    '1': 'admin-role-001',
    '2': 'delegate-role-001',
    '3': 'merchant-role-001',
    '4': 'staff-role-001',
    '5': 'support-role-001',
    '6': 'ceo-role-001',
  };

  if (legacyRoleIds[roleId]) {
    roleId = legacyRoleIds[roleId];
  }

      if (!roleId && user.role) {
        const roleName = String(user.role).toLowerCase();

        const matchingRole = roles.find(
          r => r.name?.toLowerCase() === roleName
        );

        if (matchingRole) {
          roleId = matchingRole.id;
        }
      }

      setFormData({
        fullName: user.fullName || user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        password: '',
        roleId,
        branchId: user.branchId?.toString() || '',
        isActive: user.isActive !== false
      });
    } catch (error) {
      console.error('Error loading user:', error);
      alert('حدث خطأ أثناء تحميل بيانات المستخدم');
    }
  };

  /*
   * إذا تم تحميل المستخدم قبل الأدوار وكان الـ API يعيد اسم الدور فقط،
   * نعيد مطابقة الدور بعد تحميل القائمة.
   */
  useEffect(() => {
    if (!roles.length || !id || formData.roleId) return;

    const loadUserRole = async () => {
      try {
        const response = await api.get(`/api/users/${id}`);
        const user = response.data?.data;

        if (!user?.role) return;

        const roleName = String(user.role).toLowerCase();

        const matchingRole = roles.find(
          r => r.name?.toLowerCase() === roleName
        );

        if (matchingRole) {
          setFormData(prev => ({
            ...prev,
            roleId: matchingRole.id
          }));
        }
      } catch (error) {
        console.error('Error matching user role:', error);
      }
    };

    loadUserRole();
  }, [roles, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.roleId) {
      alert('الرجاء اختيار الدور');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.fullName,
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        password: formData.password || undefined,

        /*
         * مهم:
         * نرسل ID الحقيقي القادم من AspNetRoles،
         * مثل staff-role-001 وليس رقم 4.
         */
roleId: formData.roleId,
        branchId: formData.branchId
          ? Number(formData.branchId)
          : null,

        isActive: formData.isActive
      };

      await api.put(`/api/users/${id}`, payload);

      const response = await api.get(`/api/users/${id}`);

      if (response.data.success) {
        updateUser(response.data.data);
      }

      alert('تم تحديث بيانات المستخدم بنجاح!');
      navigate('/users');

    } catch (error: any) {
      console.error('Update error:', error);
      console.error('Server response:', error.response?.data);

      alert(
        error.response?.data?.message ||
        'حدث خطأ أثناء التحديث'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent";

  const labelClass =
    "block text-sm font-bold text-gray-700 mb-1.5";

  return (
    <div className="p-4 md:p-6">

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            تعديل بيانات المستخدم
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            تعديل بيانات المستخدم والصلاحيات
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/users')}
          className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4"
      >

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div>
            <label className={labelClass}>
              الاسم الكامل *
            </label>

            <input
              type="text"
              required
              value={formData.fullName}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  fullName: e.target.value
                })
              }
              className={inputClass}
              placeholder="أدخل الاسم الكامل"
            />
          </div>

          <div>
            <label className={labelClass}>
              اسم المستخدم *
            </label>

            <input
              type="text"
              required
              value={formData.username}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  username: e.target.value
                })
              }
              className={inputClass}
              placeholder="أدخل اسم المستخدم"
            />
          </div>

          <div>
            <label className={labelClass}>
              البريد الإلكتروني *
            </label>

            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  email: e.target.value
                })
              }
              className={inputClass}
              placeholder="example@email.com"
            />
          </div>

          <div>
            <label className={labelClass}>
              رقم الهاتف
            </label>

            <input
              type="tel"
              value={formData.phone}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  phone: e.target.value
                })
              }
              className={inputClass}
              placeholder="01xxxxxxxxx"
            />
          </div>

          <div>
            <label className={labelClass}>
              كلمة المرور الجديدة
            </label>

            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  password: e.target.value
                })
              }
              className={inputClass}
              placeholder="اتركها فارغة إذا لم ترد تغييرها"
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>
              العنوان بالتفصيل
            </label>

            <input
              type="text"
              value={formData.address}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: e.target.value
                })
              }
              className={inputClass}
              placeholder="العنوان الكامل للمستخدم (لطلبات الالتقاط)"
            />
          </div>

          <div>
            <label className={labelClass}>
              الدور *
            </label>

            <select
              required
              value={formData.roleId}
              disabled={rolesLoading}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  roleId: e.target.value
                })
              }
              className={`${inputClass} ${
                rolesLoading
                  ? 'bg-gray-100 cursor-wait'
                  : ''
              }`}
            >
              <option value="">
                {rolesLoading
                  ? 'جاري تحميل الأدوار...'
                  : 'اختر الدور'}
              </option>

{roles
  .filter(role => role.isActive !== false)
  .map(role => {
    const roleName = role.name?.toLowerCase() || '';
    const backendRoleId = roleBackendIds[roleName];

    return (
      <option
        key={role.id}
        value={backendRoleId ?? ''}
      >
        {roleLabels[roleName] || role.name}
      </option>
    );
  })}
            </select>

            {!rolesLoading && roles.length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                لم يتم العثور على أدوار من الخادم
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              الحالة
            </label>

            <select
              value={formData.isActive ? 'true' : 'false'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  isActive: e.target.value === 'true'
                })
              }
              className={inputClass}
            >
              <option value="true">نشط</option>
              <option value="false">غير نشط</option>
            </select>
          </div>

        </div>

        <div className="flex justify-end gap-3 pt-4">

          <button
            type="button"
            onClick={() => navigate('/users')}
            className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition"
          >
            إلغاء
          </button>

          <button
            type="submit"
            disabled={loading || rolesLoading}
            className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />

            {loading
              ? 'جاري الحفظ...'
              : 'حفظ التعديلات'}
          </button>

        </div>

      </form>
    </div>
  );
}
